/**
 * Client for guest tags and guest links (OrchestratorAPI
 * `GroupsController.Tags.cs`). Host-gated through `CanEditTracker`, the same
 * gate the tracker board is behind — deletion alone is owner-only, which is why
 * a 403 here is a real refusal and not a bug.
 *
 * There is deliberately no singular "put one tag on one guest" route. Tagging
 * one guest is a batch of one, and two write paths for one fact is how the
 * roster grows rules that disagree.
 *
 * The failure mapping is `toTrackerFailure` with ONE change, and it is worth
 * stating plainly: a 404 here is never reported as "you are not a host". The
 * tracker gate answers 404 for not-a-host so it cannot confirm which trip ids
 * exist — but the Guests tab does not render until the board read has already
 * proved host-ness, and the tag controls do not render until that board came
 * back carrying a `tags` key, so by the time anything in this file is issued
 * both of those are settled. What is left is two other meanings, and they are
 * declared per route rather than guessed at: see `missingMeans` on
 * {@link request}. The primary capability check is still the board's `tags` key
 * (`tagsAvailable` in lib/guestTags.ts); the mapping here is the backstop for a
 * Studio bundle that outlives an API rollback.
 */

import type { AxiosRequestConfig } from 'axios';
import { orchestratorClient } from './groups-api-client';
import {
  toTrackerFailure,
  type GuestLink,
  type GuestTag,
  type TrackerFailure,
} from './tracker-api-client';

/**
 * Everything `TrackerFailure` carries, plus the two meanings a 404 can have
 * here. The conflict variant re-attaches the response body, because a refusal
 * on these routes carries the facts the host needs to decide — how many guests
 * still hold the tag, or the link that already joins this pair.
 */
export type TagFailure =
  | Exclude<TrackerFailure, { kind: 'conflict' }>
  | { kind: 'conflict'; message: string; body?: unknown }
  | { kind: 'unsupported'; message: string }
  | { kind: 'gone'; message: string };

export type TagResult<T> = { ok: true; data: T } | { ok: false; failure: TagFailure };

export const TAGS_UNSUPPORTED_MESSAGE =
  'This trip’s API does not have guest tags yet. Nothing was changed — reload once it has been updated.';

export const TAG_GONE_MESSAGE =
  'That one is no longer on this trip — another host may have removed it. Reload to see the current list.';

/**
 * `mode` decides what the assign call does to the guests it names.
 * There are two, and deliberately not a third. A `replace` mode existed,
 * scoped to the kinds supplied so that setting a dietary tag could not wipe
 * "college friends". Scoped made it survivable, not safe: replacing the
 * dietary kind across a cohort still clears every OTHER dietary tag those
 * guests hold, and the tag most likely to be alone on somebody is an
 * allergy. Nothing here has an undo.
 */
export type TagAssignMode = 'add' | 'remove';

export interface TagAssignRequest {
  tagIds: string[];
  inviteeIds: string[];
  mode: TagAssignMode;
  /** Per-assignment detail, e.g. "EpiPen in her bag". Applies to every pair written. */
  note?: string;
}

/**
 * The write's own account of itself, plus the rows it changed.
 *
 * `tags` and `guests` come back so a bulk apply needs no re-read: the caller
 * splices them into the board it already holds. `skipped` is not an error — it
 * is the guests that already had the tag, and a host who tags 137 people wants
 * to know the number that moved.
 */
export interface TagAssignResult {
  assigned: number;
  removed: number;
  skipped: number;
  tags: GuestTag[];
  /**
   * Affected guest id -> the tag ids that guest now carries, as a MAP rather
   * than a list of guests. The server sends what changed, not the people: at
   * 500 guests a list of whole guest objects is the difference between a small
   * response and one that undoes the point of not refetching. A guest whose
   * last tag was just removed comes back as an empty array, never an absent
   * key, so "unchanged" and "now has none" stay distinguishable.
   */
  guests: Record<string, string[]>;
}

/**
 * `kind` is OPTIONAL, and absent is a meaningful value. The server resolves it
 * as `request.Kind ?? seed?.Kind ?? 'group'`, so a body that always carries a
 * kind kills the seeded vocabulary: "Wheelchair" becomes a social tag still
 * holding the seed's critical flag, which the next line of the controller then
 * refuses as a 400 about a flag the host never set. See `newTagRequest`.
 */
export interface CreateTagRequest {
  label: string;
  kind?: string;
  color?: string | null;
  isCritical?: boolean;
}

/**
 * `created: false` means the slug already existed and the stored tag came back
 * unchanged. The route is idempotent on slug precisely so "add a tag" can never
 * silently mint a twin of one the host already has.
 */
export interface CreateTagResult {
  tag: GuestTag;
  created: boolean;
}

/** Absent leaves the stored value alone; null clears it. Mirrors `PatchValue<T>`. */
export interface PatchTagRequest {
  label?: string;
  color?: string | null;
  kind?: string;
  position?: number | null;
  /**
   * Whether this tag belongs on the kitchen sheet.
   *
   * Sent two ways. WITH a kind change, when a critical tag is leaving a health
   * kind — the server validates the merged pair, so the kind alone is refused
   * and the two together succeed. And ALONE, from the "On the kitchen sheet"
   * checkbox, which is the only way the flag is ever set back to true; a lone
   * `true` on a dietary or access tag passes the same merged check.
   *
   * Clearing it is confirmed by the host in both directions. Un-criticalling an
   * allergy silently is the failure the server refuses a bare kind change to
   * prevent, and a client that auto-appended `false` would be re-opening it
   * from the other side.
   */
  isCritical?: boolean;
}

/**
 * The 409 a delete answers when guests still carry the tag, unpacked.
 *
 * The refusal carries the count so Studio can offer "delete it from 12 guests
 * too" instead of a dead-end error — the same shape the seating table refusal
 * uses. `null` for any other failure, so the caller only offers force when
 * forcing is actually the fix.
 */
export interface TagInUse {
  assignedCount: number;
}

export function tagInUse(failure: TagFailure): TagInUse | null {
  if (failure.kind !== 'conflict') return null;
  const body = failure.body as Record<string, unknown> | undefined;
  if (body?.code !== 'tag_in_use') return null;
  const count = Number(body.assignedCount);
  return Number.isFinite(count) ? { assignedCount: count } : null;
}

/**
 * The link a 409 from `createGuestLink` refused to duplicate.
 *
 * The picker already hides everyone this guest is linked to, so a 409 means the
 * pair was joined by another host while this record was open. The stored row
 * comes back on the refusal, which is what lets the caller put the link on the
 * record and name what it says, instead of an error the host has to resolve by
 * hunting for a link they cannot see. There is deliberately no "change it from
 * here": a create that quietly became an edit is how a keep-apart turns into a
 * seat-together.
 */
export function conflictingLink(failure: TagFailure): GuestLink | null {
  if (failure.kind !== 'conflict') return null;
  const body = failure.body as { link?: GuestLink } | undefined;
  return body?.link?.id ? body.link : null;
}

export interface CreateGuestLinkRequest {
  inviteeId: string;
  otherId: string;
  relation: string;
  seating: string;
  strength?: string;
  note?: string;
}

// ---- request plumbing ----

/**
 * The tracker client's `request` is not exported, and exporting it would make
 * its 404 → not_host mapping this file's mapping too — which is exactly the one
 * rule that has to differ here. Six lines of duplication buys that difference;
 * the failure translation itself is still `toTrackerFailure`.
 */
async function request<T>(
  path: string,
  /**
   * What a 404 means on THIS route.
   *
   * The question is where the ids are, not how the path is spelled. A route
   * that names no row — "/tags", "/tags/health-vocabulary" — can only newly 404
   * because the API has no such route. A route that names one can 404 all the
   * time in normal use, because another host deleted it, and that holds whether
   * the id is in the path ("/tags/{tagId}") or in the BODY: `/tags/assign` and
   * `/guest-links` answer 404 for a tag, an invitee or the other end of a link
   * that is no longer on the trip. Telling that host their API is out of date
   * would be a lie, and telling the first host their tag was deleted would be
   * a different one, so the caller says which question it is asking.
   */
  missingMeans: 'unsupported' | 'gone',
  config: AxiosRequestConfig = {},
): Promise<TagResult<T>> {
  try {
    const { data } = await orchestratorClient.request<T>({
      url: path,
      headers: { Accept: 'application/json' },
      ...config,
    });
    return { ok: true, data };
  } catch (error) {
    const failure = toTrackerFailure(error);
    if (failure.kind === 'not_host') {
      return missingMeans === 'unsupported'
        ? { ok: false, failure: { kind: 'unsupported', message: TAGS_UNSUPPORTED_MESSAGE } }
        : { ok: false, failure: { kind: 'gone', message: TAG_GONE_MESSAGE } };
    }
    if (failure.kind === 'conflict') {
      // toTrackerFailure keeps only the message; the body is what makes a
      // refusal actionable here, so it is re-attached.
      const body = (error as { response?: { data?: unknown } })?.response?.data;
      return { ok: false, failure: { ...failure, body } };
    }
    return { ok: false, failure };
  }
}

const groupPath = (groupId: string) => `/api/groups/${encodeURIComponent(groupId)}`;

/** What a host reads when a tag write is refused. */
export function tagFailureMessage(failure: TagFailure): string {
  switch (failure.kind) {
    case 'unauthorized':
      return 'Your session expired. Sign in again.';
    case 'not_host':
      return "You're not a host on this trip.";
    default:
      return failure.message;
  }
}

// ---- endpoints ----

export function createTag(
  groupId: string,
  body: CreateTagRequest,
): Promise<TagResult<CreateTagResult>> {
  return request<CreateTagResult>(`${groupPath(groupId)}/tags`, 'unsupported', {
    method: 'POST',
    data: body,
  });
}

export function patchTag(
  groupId: string,
  tagId: string,
  body: PatchTagRequest,
): Promise<TagResult<GuestTag>> {
  // The body goes straight through: JSON.stringify keeps explicit nulls (clear
  // the colour) and drops undefined (leave it alone), which is the PatchValue
  // contract. Nothing may sit in between stripping nullish values.
  return request<GuestTag>(`${groupPath(groupId)}/tags/${encodeURIComponent(tagId)}`, 'gone', {
    method: 'PATCH',
    data: body,
  });
}

/**
 * Without `force`, a tag still on somebody answers 409 and nothing is written.
 * That refusal is the feature: deleting "nut allergy" off twelve guests by
 * accident is not recoverable from this screen.
 */
export async function deleteTag(
  groupId: string,
  tagId: string,
  force = false,
): Promise<TagResult<null>> {
  const result = await request<unknown>(
    `${groupPath(groupId)}/tags/${encodeURIComponent(tagId)}${force ? '?force=true' : ''}`,
    'gone',
    { method: 'DELETE' },
  );
  // 204 — axios hands back an empty string, so normalize it to the null the
  // callers expect.
  return result.ok ? { ok: true, data: null } : result;
}

/**
 * Everything dietary or access this trip holds, after the seeded vocabulary has
 * been inserted. Not the whole palette — the route answers with the two health
 * kinds only, so the caller merges rather than replaces.
 */
export interface HealthVocabularyResult {
  tags: GuestTag[];
}

/**
 * Puts the seeded allergy, diet and mobility entries on this trip, and hands
 * back every health tag it now has.
 *
 * The route exists because the design says those twenty-odd rows arrive "on
 * first use of the Health & access picker" rather than on every trip at
 * creation, so something has to say the picker is open — nothing else calls it.
 * It is idempotent: `ON CONFLICT DO NOTHING` server-side, and a slug the host
 * already made by hand is left exactly as they made it.
 *
 * The caller must not wait on this before drawing the picker. A host on a slow
 * connection should watch the palette fill in, not watch a spinner, and a
 * refusal here leaves the palette precisely as it already was.
 */
export function seedHealthVocabulary(
  groupId: string,
): Promise<TagResult<HealthVocabularyResult>> {
  return request<HealthVocabularyResult>(
    `${groupPath(groupId)}/tags/health-vocabulary`,
    'unsupported',
    { method: 'POST' },
  );
}

/**
 * One transaction, one round trip, up to 500 guests and 10 tags.
 *
 * This is the endpoint the whole feature stands on: tagging a cohort has to be
 * one action, because one-at-a-time tagging is why a host would never tag
 * anyone at all.
 */
export function assignTags(
  groupId: string,
  body: TagAssignRequest,
): Promise<TagResult<TagAssignResult>> {
  // 'gone', not 'unsupported': the ids are in the body, and the server answers
  // 404 when a tag or an invitee named there is not on the trip — which is what
  // happens when another host deletes the tag while this palette is open.
  return request<TagAssignResult>(`${groupPath(groupId)}/tags/assign`, 'gone', {
    method: 'POST',
    data: body,
  });
}

/**
 * The server canonicalizes the pair ordering, so the caller passes the two ids
 * in whatever order the host picked them. A 409 means the pair is already
 * linked and carries the stored link, so the caller can put that link on the
 * record and say what it holds — see {@link conflictingLink}, which is also
 * where the reason there is no "change it from here" is written down.
 *
 * 'gone' rather than 'unsupported': both ids are in the body, so a 404 means
 * one of the two people is no longer on this trip, not that the route is
 * missing.
 */
export function createGuestLink(
  groupId: string,
  body: CreateGuestLinkRequest,
): Promise<TagResult<GuestLink>> {
  return request<GuestLink>(`${groupPath(groupId)}/guest-links`, 'gone', {
    method: 'POST',
    data: body,
  });
}

export async function deleteGuestLink(
  groupId: string,
  linkId: string,
): Promise<TagResult<null>> {
  const result = await request<unknown>(
    `${groupPath(groupId)}/guest-links/${encodeURIComponent(linkId)}`,
    'gone',
    { method: 'DELETE' },
  );
  return result.ok ? { ok: true, data: null } : result;
}
